/**
 * Shared Systems-Integrations State
 *
 * Contains data shared between the per-kind integration subdomains
 * (channels, erps, ...):
 * - Connectors (the catalog of available connectors, capability-aware)
 * - Active tab
 *
 * Renamed from the sales-channel-centric `SharedIntegrationsState` to the
 * systems-integrations naming. The store namespace path (`integrations/*`)
 * is intentionally preserved for command compatibility.
 */

import {
  SystemIntegrationConnectorReadmodel,
  ConnectorKind,
  Capability
} from '@bett3r-dev/teselly-domain';

/**
 * A catalog connector entry, carrying its `ConnectorKind` and declared
 * `capabilities` so the UI can group and gate setup flows without a
 * connector-id switch.
 *
 * Note: the readmodel already has a `connectorKind` field (`custom | built-in`,
 * the connector *implementation* kind). The capability-grouping kind
 * (`salesChannel | ERP | ...`) is carried here as `kind`, derived from the
 * readmodel's `type` field.
 */
export type ConnectorEntry = Omit<SystemIntegrationConnectorReadmodel, 'connectorKind'> & {
  /** `salesChannel | ERP | invoicing | stockProvider | CRM | marketing` */
  connectorKind: ConnectorKind;
  /** Declared capabilities (`oauth`, `api-key`, `syncs-orders`, ...) */
  capabilities: Capability[];
};

export type SystemsIntegrationsState = {
  /** Available connectors keyed by connector id (e.g. mercadolibre, arcaInvoicing) */
  connectors: Record<string, ConnectorEntry>;

  /** Currently active tab */
  activeTab: 'channels' | 'erps' | 'invoicing' | 'logistics' | 'messaging';

  /** Loading states */
  loading: {
    connectorsLoading: boolean;
  };
};

export const initialState: SystemsIntegrationsState = {
  connectors: {},
  activeTab: 'channels',
  loading: {
    connectorsLoading: false
  }
};

/**
 * Resolves the `ConnectorKind` for a connector entry. The readmodel's `type`
 * field already holds the `ConnectorKind`; this normalizes legacy `'erp'`
 * casing defensively.
 */
const resolveConnectorKind = ( connector: ConnectorEntry ): ConnectorKind => {
  if ( connector.connectorKind ) return connector.connectorKind;
  const type = connector.type as string;
  if ( type === 'erp' ) return 'ERP';
  return type as ConnectorKind;
};

/**
 * Selectors for shared systems-integrations state.
 */
export const selectors = {
  getConnector: ( state: SystemsIntegrationsState, connectorId: string ): ConnectorEntry | undefined =>
    state.connectors[connectorId],

  connectorsLoading: ( state: SystemsIntegrationsState ): boolean =>
    state.loading.connectorsLoading,

  /**
   * All catalog connectors whose `connectorKind` matches `kind`.
   */
  selectConnectorsByKind: (
    state: SystemsIntegrationsState,
    kind: ConnectorKind
  ): ConnectorEntry[] =>
    Object.values( state.connectors ).filter(
      ( connector ) => resolveConnectorKind( connector ) === kind
    ),

  /**
   * All catalog connectors grouped by `connectorKind`, for the
   * kind-grouped connector list.
   */
  selectIntegrationsByKind: (
    state: SystemsIntegrationsState
  ): Record<ConnectorKind, ConnectorEntry[]> => {
    const grouped = {} as Record<ConnectorKind, ConnectorEntry[]>;
    Object.values( state.connectors ).forEach(( connector ) => {
      const kind = resolveConnectorKind( connector );
      if ( !grouped[kind]) grouped[kind] = [];
      grouped[kind].push( connector );
    });
    return grouped;
  },

  /**
   * Catalog connectors of `kind: 'salesChannel'`, keyed by connector id.
   * Convenience wrapper over `selectConnectorsByKind` that preserves the
   * `Record<connectorId, ConnectorEntry>` shape used by the channels subdomain
   * (e.g., `connectors[connectorId]` lookups in cards/lists).
   */
  salesChannelConnectors: ( state: SystemsIntegrationsState ): Record<string, ConnectorEntry> => {
    const result: Record<string, ConnectorEntry> = {};
    Object.entries( state.connectors ).forEach(([ key, value ]) => {
      if ( resolveConnectorKind( value ) === 'salesChannel' ) {
        result[key] = value;
      }
    });
    return result;
  },

  /**
   * Catalog connectors of `kind: 'ERP'`, keyed by connector id. Convenience
   * wrapper used by the erps subdomain. Only ERP connectors — invoicing / CRM /
   * marketing / stockProvider connectors are intentionally excluded so they
   * don't surface in the "Add ERP" flow. For other kinds use
   * `selectConnectorsByKind`.
   */
  erpConnectors: ( state: SystemsIntegrationsState ): Record<string, ConnectorEntry> => {
    const result: Record<string, ConnectorEntry> = {};
    Object.entries( state.connectors ).forEach(([ key, value ]) => {
      if ( resolveConnectorKind( value ) === 'ERP' ) {
        result[key] = value;
      }
    });
    return result;
  }
};
